import { AfterViewInit, Component, computed, input, output, ViewChild } from "@angular/core";
import { LiveAnnouncer } from "@angular/cdk/a11y";
import { MatSort, MatSortModule, Sort } from "@angular/material/sort";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatMenuModule } from "@angular/material/menu";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatTable, MatTableDataSource, MatTableModule } from "@angular/material/table";
import { SortingDirective } from './sorting.directive';
import { Subscription } from "rxjs";
import { MODULE_TYPE } from "../../../../../commondata/messages/msg-constants";
import { AuthenticationService } from "../../core/authentication/authentication.service";
import { DocDto } from "../../dto/doc-dto";
import { OpenDocInfoDto } from "../../dto/open-doc-info-dto";
import { TrashDocService } from "../../services/trash-doc.service";
import { ShellEmittersService } from "../../shell-emitters.service";
import { ShellStateService } from "../../shell-state.service";
import { ModuleSpacesService } from "../../workspace/module-spaces.service";
import { WorkspaceService } from "../../workspace/workspace.service";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { FirebaseService } from "../../core/firebase/firebase.service";

@Component({
    selector: "app-doc-picker-table",
    imports: [
        CommonModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatButtonToggleModule,
        MatIconModule,
        MatSortModule,
        MatTableModule,
        ReactiveFormsModule,
        MatMenuModule,
        TranslateModule,
        MatTooltipModule,
        SortingDirective,
    ],
    templateUrl: "./doc-picker-table.component.html",
    styleUrl: "./doc-picker-table.component.scss"
})
export class DocPickerTableComponent implements AfterViewInit {

  searchText: string = '';
  openDocs: Array<OpenDocInfoDto | null> = [];
  type = input<string>('');
  files = input<DocDto[]>([]);
  selected = output<DocDto>();
  doubleClicked = output<DocDto>();

  MODULE_TYPE = MODULE_TYPE; // Pubblica per uso in HTML
  displayedColumns: string[] = [
    "preview",
    "doctype",
    "name",
    "lastOpened",
    "sharestate"
  ];
  // columnsToDisplayWithExpand = [...this.displayedColumns, 'expand'];
  //expandedElement: DocDto | null | undefined;
  //dataSource = new MatTableDataSource();
  dataSource = new MatTableDataSource<DocDto>;//WritableSignal<DocDto[]> = signal([]);

  // _linkedFile: WritableSignal<DocDto[]> = signal([]);
  // public linkedFile = computed(() => this._linkedFile());

  filter: "all" | "recent" = "recent";
  selectedFiles = computed(() =>
    this.files().filter((f) =>
      this.filter == "all" ? this.files() : this.files()
    )
  ); // TODO filtro dei recenti

  @ViewChild(MatSort) sort!: MatSort | null;
  @ViewChild(MatTable) table: MatTable<DocDto> | undefined;

  isListCompressed = false;
  hideIcons = false;
  ellipsisName = false;
  selectedDoc: DocDto | null = null;
  private loadedAllDocumentsSubscription: Subscription | undefined;

  public NoFilesDescriptor = '';

  constructor(
    private _liveAnnouncer: LiveAnnouncer,
    public shellStateService: ShellStateService,
    public shellEmittersService: ShellEmittersService,
    public trashDocService: TrashDocService,
    public moduleSpacesService: ModuleSpacesService,
    private workspaceService: WorkspaceService,
    public authenticationService: AuthenticationService,
    public firebaseService: FirebaseService,
  ) {
    this.openDocs = this.moduleSpacesService.getOpenDocs();
    this.loadedAllDocumentsSubscription?.unsubscribe();
    this.loadedAllDocumentsSubscription = this.workspaceService.loadedAllDocuments.subscribe((res: boolean) => {
      setTimeout(() => {
        const data = this.files();
        this.dataSource.data = [];
        this.dataSource.data = data;
        this.dataSource.sort = this.sort;
      }, 10);
    });
    this.dataSource.sort = this.sort;

    // Setup filter predicate to search in name field
    this.dataSource.filterPredicate = (data: DocDto, filter: string) => {
      return data.name.toLowerCase().includes(filter);
    };
  }

  ngAfterViewInit() {
    setTimeout(() => {
      const data = this.files();
      this.dataSource.data = data;
      this.dataSource.sort = this.sort;
    }, 5);
    console.log("ngAfterViewInit");
  }

  /** Announce the change in sort state for assistive technology. */
  announceSortChange(sortState: Sort | any) {
    // This example uses English messages. If your application supports
    // multiple language, you would internationalize these strings.
    // Furthermore, you can customize the message to add additional
    // details about the values being sorted.
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce("Sorting cleared");
    }
    // localStorage.setItem('sortState', JSON.stringify(sortState) );
    this.dataSource.data = [];
    this.dataSource = new MatTableDataSource(this.files());
    this.dataSource.sort = this.sort;
    if (this.table !== undefined) {
      this.table.renderRows();
    }
  }

  selectedFile(doc: DocDto) {
    console.log(`SELECTED DOC: ${doc.id} - ${doc.name}`);
    this.selectedDoc = doc;
    this.selected.emit(doc);
  }

  doubleClickedFile(doc: DocDto) {
    this.selectedFile(doc);
    // Emit through the double click output
    this.doubleClicked.emit(doc);
  }

  isCurrentlyOpen(doc: DocDto) {
    const opened = this.openDocs?.findIndex((d) => d?.id == doc.id) > -1;
    doc.expanded = opened && doc.linkedDocs.length > 0 ? true : false;
    return opened;
  }

  public getDocIconStyle(doc: DocDto) {
    switch (doc.docType) {
      case MODULE_TYPE.MAP:
        return this.authenticationService.isMapEnabled() ? doc.docType : 'document-not-active';
      case MODULE_TYPE.EDITOR:
        return this.authenticationService.isEditorEnabled() ? doc.docType : 'document-not-active';
      case MODULE_TYPE.PDF:
        return this.authenticationService.isPdfEnabled() ? doc.docType : 'document-not-active';
      default:
        return '';
    }
  }

  public copyDocId(doc: DocDto) {
    if (doc) navigator.clipboard.writeText(doc.id);
  }

  clearSearch() {
    this.searchText = '';
    this.applyFilter();
  }

  applyFilter() {
    const filterValue = this.searchText.trim().toLowerCase();
    this.dataSource.filter = filterValue;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }


}
